import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CONTRIBUTORS } from '../data/contributors.js';
import StaticSeo from '../components/StaticSeo.jsx';

// ─────────────────────────────────────────────────────────────────────────
// CONTRIBUTOR INDEX — /contributors
// Editorial masthead. Five-card grid of the people who write for the site.
// Each card links to /contributors/:slug.
// ─────────────────────────────────────────────────────────────────────────

export default function ContributorIndex() {
  const [dark, setDark] = useState(false);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <StaticSeo schemaType="collectionPage" path="/contributors" />
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }

        .contributor-card {
          transition: background 0.18s, border-color 0.18s, transform 0.18s;
        }
        .contributor-card:hover {
          background: ${T.surface};
          border-color: ${T.sienna};
        }
        .contributor-card:hover .arrow {
          transform: translateX(4px);
          color: ${T.sienna};
        }
        .arrow { transition: transform 0.18s, color 0.18s; }
        .ghost-link:hover { color: ${T.sienna}; }

        @media (max-width: 820px) {
          .contributor-grid { grid-template-columns: 1fr !important; }
          .container { padding: 24px 20px 60px !important; }
          .index-h1 { font-size: 56px !important; }
        }
      `}</style>

      <div className="container" style={{ maxWidth: 1080, margin: '0 auto', padding: '32px 28px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 24, letterSpacing: '-0.015em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Section kicker */}
        <div className="reveal" style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 16,
        }}>
          § THE MASTHEAD
        </div>

        {/* Title */}
        <h1 className="index-h1 reveal" style={{
          fontFamily: fontDisplay, fontSize: 88, fontWeight: 400,
          lineHeight: 1.0, letterSpacing: '-0.03em',
          margin: '0 0 20px',
        }}>
          The five voices, <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>one trade</em>.
        </h1>
        <p className="reveal" style={{
          fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
          lineHeight: 1.55, color: T.inkSoft,
          margin: '0 0 56px', maxWidth: 660,
        }}>
          Working cleaners, a science editor who wandered in from materials chemistry, and a field correspondent who keeps filing copy at three in the morning. Everything on this site is written by one of them.
        </p>

        {/* Cards grid */}
        <div className="contributor-grid" style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 20,
        }}>
          {CONTRIBUTORS.map((c) => (
            <Link
              key={c.slug}
              to={`/contributors/${c.slug}`}
              className="contributor-card"
              style={{
                display: 'flex', flexDirection: 'column',
                textDecoration: 'none', color: T.ink,
                border: `1px solid ${T.rule}`,
                padding: '28px 28px 24px',
                minHeight: 340,
              }}
            >
              {/* Top row: monogram + role */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 16,
                marginBottom: 24,
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: T.surface, border: `1px solid ${T.rule}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                  color: T.inkSoft,
                }}>
                  {c.initial}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 4,
                  }}>
                    {c.fullTitle.toUpperCase()}
                  </div>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.inkFaint,
                  }}>
                    {c.location.toUpperCase()} · {c.tenure.toUpperCase()}
                  </div>
                </div>
              </div>

              {/* Name */}
              <h2 style={{
                fontFamily: fontDisplay, fontSize: 30, fontWeight: 400,
                lineHeight: 1.1, letterSpacing: '-0.02em',
                margin: '0 0 12px',
              }}>
                {c.name}
              </h2>

              {/* Short bio */}
              <p style={{
                fontFamily: fontDisplay, fontSize: 16, fontWeight: 300,
                lineHeight: 1.55, color: T.inkSoft,
                margin: '0 0 20px', flex: 1,
              }}>
                {c.shortBio}
              </p>

              {/* Beats */}
              <div style={{
                display: 'flex', gap: 6, flexWrap: 'wrap',
                marginBottom: 18,
              }}>
                {c.beats.slice(0, 3).map((beat) => (
                  <span key={beat} style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.1em', color: T.inkSoft,
                    border: `1px solid ${T.rule}`,
                    padding: '4px 10px',
                  }}>
                    {beat.toUpperCase()}
                  </span>
                ))}
              </div>

              {/* Footer arrow */}
              <div style={{
                paddingTop: 16, borderTop: `1px solid ${T.rule}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                }}>
                  READ THE FULL BIO
                </span>
                <span className="arrow" style={{
                  fontFamily: fontMono, fontSize: 13, color: T.inkFaint,
                }}>→</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Footer */}
        <footer style={{
          marginTop: 80, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <Link to="/" className="ghost-link" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO HOMEPAGE
          </Link>
          <span style={{
            fontFamily: fontMono, fontSize: 10,
            letterSpacing: '0.15em', color: T.inkFaint,
          }}>
            © 2026 WINDOW WASHING GUIDE
          </span>
        </footer>
      </div>
    </div>
  );
}
